'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { CLASS_TRACKING_GTM } from 'constants/index';
import { categoriesFilters } from '../Header/components/NewHeaderContext/components/HeaderFilters/categoriesFilters';
import { getHighlightedCollections } from '../Header/components/utils/getHighlightedColletions';
import { CollectionCard } from 'components/Shared';
import { getMainUrl } from 'utils/misc';
import { analytics, analyticsEvents, transportMethods } from 'lib/analytics';
import * as styles from './HighlightCollections.css';

function CollectionItemSkeleton({
  isMobileVariant = false
}: {
  isMobileVariant?: boolean;
}) {
  return (
    <div
      className={`${styles.skeletonItemWrapper} ${styles.skeletonPulse} ${isMobileVariant ? styles.skeletonItemMobile : ''}`}
    ></div>
  );
}

interface CollectionItemProps {
  collection: {
    label: string;
    link: string;
    icon: string;
  };
}

function CollectionItem({ collection }: CollectionItemProps) {
  return (
    <a
      href={getMainUrl() + collection.link}
      key={collection.link}
      className={`${CLASS_TRACKING_GTM.COLLECTION_CARD} ${styles.itemCollection}`}
      data-bannerid={collection.link}
      data-name={collection.label}
      onClick={() => {
        analytics.sendEvent(analyticsEvents.search_recommended_clicked, {
          search_recommendation_collections_clicked: collection.label,
          transport: transportMethods.sendBeacon
        });
      }}
    >
      <Image
        src={collection.icon}
        alt={collection.label}
        width={24}
        height={24}
      />
      {collection.label}
    </a>
  );
}

type CollectionHighlighted = {
  label: string;
  link: string;
  icon: string;
  highlighted?: string;
};

interface HighlightCollectionsProps {
  isMobileVariant?: boolean;
}

function HighlightCollections({
  isMobileVariant = false
}: HighlightCollectionsProps) {
  const [collections, setCollections] = useState<CollectionHighlighted[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const SKELETON_COUNT = 5;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const highlighted = await getHighlightedCollections();
        setCollections(highlighted.length ? highlighted : categoriesFilters);
      } catch (e) {
        setCollections(categoriesFilters);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const renderSkeletons = useMemo(
    () =>
      Array.from({ length: SKELETON_COUNT }).map((_, index) => (
        <CollectionItemSkeleton
          key={`skeleton-${index}`}
          isMobileVariant={isMobileVariant}
        />
      )),
    [isMobileVariant]
  );

  const renderCollections = useMemo(
    () =>
      collections.map((collection) =>
        isMobileVariant ? (
          <CollectionCard
            key={collection.link}
            isLoading={false}
            images={{ icon: collection.icon }}
            label={collection.label}
            href={getMainUrl() + collection.link}
            onClick={() => {
              analytics.sendEvent(analyticsEvents.search_recommended_clicked, {
                search_recommendation_collections_clicked: collection.label,
                transport: transportMethods.sendBeacon
              });
            }}
          />
        ) : (
          <CollectionItem key={collection.link} collection={collection} />
        )
      ),
    [isMobileVariant, collections]
  );

  const title = isMobileVariant ? 'Coleções em alta' : 'Explore as coleções';

  return (
    <div className={styles.sectionWrapper}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.collectionsContainer}>
        {isLoading ? renderSkeletons : renderCollections}
      </div>
    </div>
  );
}

export default HighlightCollections;
